package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.mystybag.appCore.logic.pages.ProfilePageViewModel
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.extensions.formatAmount
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p14
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

@Composable
fun ProfilePageView(viewModel: ProfilePageViewModel) {
    BottomSheetUIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        removeVerticalPadding = true,
        navigationContent = null,
        applyBottomInsetSpacer = false,
        showTopBar = false,
        bottomSheetFactory = {}
    ) { uiState ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = p16),
            horizontalAlignment = Alignment.Start
        ) {
            var title = sharedString(SR.strings.menu)
            if(uiState.value.userName.isNotEmpty() && uiState.value.userName != " ")
                title = "Hi, ${uiState.value.userName}"

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = p32)
                    .height(44.dp),
                contentAlignment = Alignment.CenterStart
            ) {
                Texts.ResizableText(
                    text = title,
                    style = AppTheme.current.typography.displaySmall,
                    color = AppTheme.current.colors.onSurface,
                    minSize = 16.sp,
                    modifier = Modifier.fillMaxWidth()
                )
            }

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
            ) {
                Spacer(modifier = Modifier.height(p16))

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(IntrinsicSize.Min)
                        .border(
                            width = 1.dp,
                            color = AppTheme.current.colors.surfaceContainer,
                            shape = RoundedCornerShape(p32)
                        )
                        .padding(horizontal = p48, vertical = p20),
                    horizontalArrangement = Arrangement.spacedBy(p48)
                ) {
                    Column(
                        modifier = Modifier.weight(1f),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Texts.ResizableText(
                            text = "${uiState.value.numberOfReservations}",
                            style = AppTheme.current.typography.headlineMedium,
                            color = AppTheme.current.colors.onSurface,
                            minSize = 20.sp
                        )
                        Texts.LabelMedium(
                            text = stringResource(SR.strings.total_reservations),
                            color = AppTheme.current.colors.onSurfaceVariant,
                            textAlign = TextAlign.Center
                        )
                    }
                    Box(
                        modifier = Modifier
                            .width(p1)
                            .fillMaxHeight()
                            .background(AppTheme.current.colors.onSurfaceVariant.copy(alpha = 0.3f))
                    )
                    Column(
                        modifier = Modifier.weight(1f),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Texts.ResizableText(
                            text = "${uiState.value.savedAmount.toString().formatAmount()} €",
                            style = AppTheme.current.typography.headlineMedium,
                            color = AppTheme.current.colors.onSurface,
                            minSize = 20.sp
                        )
                        Texts.LabelMedium(
                            text = stringResource(SR.strings.not_picked_reservations),
                            color = AppTheme.current.colors.primary,
                            textAlign = TextAlign.Center
                        )
                    }
                }

                Spacer(modifier = Modifier.height(p24))

                // Frame 135381: itemSpacing 24
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(p12)
                ) {
                    // List Group 1 – ACCOUNT (3 items)
                    ProfileSection(
                        sectionTitle = sharedString(SR.strings.section_account),
                        items = listOf(
                            sharedString(SR.strings.profile) to { viewModel.onProfileClick() },
                            sharedString(SR.strings.notifications) to { viewModel.onNotificationsClick() }
                        )
                    )

                    // List Group 2 – MYSTYBAG (5 items)
                    ProfileSection(
                        sectionTitle = sharedString(SR.strings.section_mystybag),
                        items = listOf(
                            sharedString(SR.strings.settings) to { viewModel.onSettingsClick() },
                            sharedString(SR.strings.about_mystybag) to { viewModel.onAboutMystyBagClick() },
                            sharedString(SR.strings.terms_of_use) to { viewModel.onTermsOfUseClick() },
                            sharedString(SR.strings.privacy_policy) to { viewModel.onPrivacyPolicyClick() },
                            sharedString(SR.strings.contacts) to { viewModel.onHelpCenterClick() },
                        )
                    )
                }

                Spacer(modifier = Modifier.height(p32))

                // Log out button (half width, centered)
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.Center
                ) {
                    DefaultButton(
                        modifier = Modifier.fillMaxWidth(0.5f),
                        caption = stringResource(SR.strings.log_out),
                        type = DefaultButtonType.DANGER,
                        callback = viewModel::onLogOutClick
                    )
                }

                Spacer(modifier = Modifier.height(p32))

                // App version 1.0.0 – 380x16
                Texts.BodySmall(
                    text = sharedString(SR.strings.app_version, uiState.value.appVersion),
                    color = AppTheme.current.colors.onSurfaceVariant,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = p8)
                        .height(p16),
                    textAlign = TextAlign.Center
                )
            }
        }
    }
}

@Composable
private fun ProfileSection(
    sectionTitle: String,
    items: List<Pair<String, () -> Unit>>
) {
    // Същата структура като MenuView: section header + itemSpacing 8 между редовете
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(p0)
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(p12)
        ) {
            Texts.LabelLarge(
                text = sectionTitle.uppercase(),
                color = AppTheme.current.colors.onSurfaceVariant,
                modifier = Modifier
                    .fillMaxWidth()
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(p1)
                    .background(AppTheme.current.colors.outline)
            )
        }

        // List items: 60dp височина, itemSpacing 8 между редовете (като MenuView AccountListItem)
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(p8)
        ) {
            items.forEach { (label, onClick) ->
                ProfileListItem(
                    text = label,
                    onClick = onClick
                )
            }
        }
    }
}

@Composable
private fun ProfileListItem(
    text: String,
    onClick: () -> Unit
) {
    // Същият ред като MenuView AccountListItem: 60dp, padding 8, roundPadding 8, текст + стрелка
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(p48)
            .rippleClick(padding = p8, roundPadding = p8, action = onClick)
    ) {
        Texts.TitleMedium(
            text = text,
            color = AppTheme.current.colors.onSurface,
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.CenterStart)
        )
        androidx.compose.foundation.Image(
            painter = painterResource(SRC.images.right_arrow),
            contentDescription = null,
            contentScale = androidx.compose.ui.layout.ContentScale.Fit,
            modifier = Modifier
                .size(width = p8, height = p14)
                .align(Alignment.CenterEnd),
            colorFilter = ColorFilter.tint(AppTheme.current.colors.primary)
        )
    }
}
